import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { QueryObserverResult, useMutation } from 'react-query';

import {
  HomeViewType,
  IBookingsFilter,
  BookingListType,
  IPlan,
  IUserInfo,
} from '../global/interfaces';
import { IBookings } from '../global/interfaces';
import { IUserDocumentTemplate, IBooking } from '@homeporter/types';
import useBookings from '../hooks/useBookings';
import useSession from '../hooks/useSession';
import useBookingDescription from '../hooks/useBookingDescription';
import useUserProfile from '../hooks/useUserProfile';

import toggleToast from '../utils/messaging';
import { groupAndSortBookings } from '../utils/helpers';

import { COLORS } from '../utils/colors';

const useCalendarStyles = () => {
  return {
    SpotBookingIcon: {
      borderRadius: '1rem',
      width: '10px',
      height: '10px',
      backgroundColor: COLORS.PRIMARY,
      marginRight: '0.25rem',
      marginLeft: '0.25rem',
    },
    ProblemSolverIcon: {
      borderRadius: '1rem',
      width: '10px',
      height: '10px',
      backgroundColor: '#bb1977',
      marginRight: '0.25rem',
      marginLeft: '0.25rem',
    },
    BundleHelpIcon: {
      borderRadius: '1rem',
      width: '10px',
      height: '10px',
      backgroundColor: 'rgb(64, 129, 195)',
      marginRight: '0.25rem',
      marginLeft: '0.25rem',
    },
    UnlimitedHelpIcon: {
      borderRadius: '1rem',
      width: '10px',
      height: '10px',
      backgroundColor: 'rgb(22, 157, 11)',
      marginRight: '0.25rem',
      marginLeft: '0.25rem',
    },
  };
};

const defaultState: IBookings = {
  gitHash: '',
  bookingsFilter: { status: 1 },
  setBookingsFilter: (_: IBookingsFilter) => {},

  bookings: [],
  isFetchingBookings: false,
  errorFetchingBookings: false,

  bookingListType: 'active',
  setBookingListType: (): void => {},

  homeView: 'schedule',
  setHomeView: (): void => {},

  refetchBookings: () => Promise.resolve(undefined),

  isFetchingSessionDetails: true,

  groupedBookings: [],

  selectedBooking: {},
  setSelectedBooking: (): void => {},

  description: '',
  yearBuilt: '',
  purchaseDate: '',
  contextPresent: false,
  setContextPresent: (): void => {},
  context: '',
  setContext: () => {},

  isBookingModalOpen: false,
  setIsBookingModalOpen: (): void => {},
  bookingModalUrl: '',
  setBookingModalUrl: (): void => {},

  isManageReportModalOpen: false,
  setIsManageReportModalOpen: (): void => {},

  isSessionSummaryModalOpen: false,
  setIsSessionSummaryModalOpen: (): void => {},

  updateBookingStatus: undefined,
  updateStatusError: undefined,

  updateDetails: undefined,
  updateDetailsLoading: undefined,
  updateDetailsError: undefined,

  plans: [],
  setPlans: (): void => {},
  updatePlan: (_: IPlan) => Promise.resolve(),

  services: [],
  setServices: (): void => {},
  documentTemplates: [],
  setDocumentTemplates: (_: IUserDocumentTemplate[]): void => {},

  updateDocumentTemplate: (_: IUserDocumentTemplate) => Promise.resolve(),
  todaysBookings: undefined,
  toolbarLabel: '',
  setToolbarLabel: (): void => {},

  getIconStyle: (title: string): React.CSSProperties => ({}),

  isDeleteBookingModalOpen: false,
  setIsDeleteBookingModalOpen: (): void => {},

  filters: {},
  setFilters: (): void => {},

  filterResults: [],
  setFilterResults: (): void => {},

  currentDate: new Date(),
  setCurrentDate: (): void => {},

  handleToolbarNavigate: (): void => {},

  currentView: 'active',
  setCurrentView: (): void => {},

  isLoading: false,
  userSettings: {},
  updateUserSettings: (_: any) => Promise.resolve(),

  organization: { name: '' },

  selectedPlan: null,
  setSelectedPlan: (): void => {},

  createdBooking: null,
  setCreatedBooking: (): void => {},
  userInfo: null,
  setUserInfo: (): void => {},
  getUser: (_: number) => Promise.resolve(null),

  editBooking: null,
  setEditBooking: (): void => {},
};

const BookingsContext = createContext(defaultState);

export function BookingsProvider({ children }: { children: JSX.Element }) {
  const [homeView, setHomeView] = useState<HomeViewType>('schedule');
  const [bookingListType, setBookingListType] =
    useState<BookingListType>('active');

  const calendarStyles = useCalendarStyles();
  const [filters, setFilters] = useState<IBookingsFilter>({});
  const [currentView, setCurrentView] = useState<'active' | 'archived'>(
    'active',
  );
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const [bookingModalUrl, setBookingModalUrl] = useState('');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isManageReportModalOpen, setIsManageReportModalOpen] = useState(false);
  const [isDeleteBookingModalOpen, setIsDeleteBookingModalOpen] =
    useState(false);
  const [isSessionSummaryModalOpen, setIsSessionSummaryModalOpen] =
    useState<boolean>(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [todaysBookings, setTodaysBookings] = useState<any | null>(null);
  const [toolbarLabel, setToolbarLabel] = useState<string>('');
  const [filterResults, setFilterResults] = useState<IBooking[]>([]);
  const [editBooking, setEditBooking] = useState<IBooking | null>(null);

  const {
    gitHash,
    profile,
    isLoading,
    updateProfile,

    userSettings,
    updateUserSettings,
    organization,

    plans,
    setPlans,
    updatePlan,

    services,
    setServices,

    documentTemplates,
    setDocumentTemplates,
    updateDocumentTemplate,

    getUser,
  } = useUserProfile();

  const {
    bookingsFilter,
    setBookingsFilter,

    bookings,
    isFetchingBookings,
    errorFetchingBookings,

    current,
    refetchCurrentBookings,
    past,
    refetchPastBookings,

    updateBookingStatus,
    updateStatusError,

    updateDetails,
    updateDetailsLoading,
    updateDetailsError,
  } = useBookings();

  const { contextRegex } = useBookingDescription();

  const {
    description,
    yearBuilt,
    purchaseDate,
    context,
    contextPresent,
    setContextPresent,
    setContext,
    parseBookingDescription,
  } = useBookingDescription();

  useEffect(() => {
    if (!selectedBooking) return;

    console.log(
      ':::::useEffect selectedBooking from BookingContext:',
      selectedBooking,
    );
    parseBookingDescription(selectedBooking);
  }, [selectedBooking]);

  const { sessionData, isFetchingSessionDetails, refetchSessionDetails } =
    useSession(
      !selectedBooking?.booking_id ? '' : String(selectedBooking?.booking_id),
    );

  const refetchBookings = () => {
    console.log('::::refetch bookings....');

    let result = undefined;
    switch (bookingListType) {
      case 'active':
        result = refetchCurrentBookings();
        break;

      case 'archived':
        result = refetchPastBookings();
        break;

      case 'month':
        console.log('fetch this months bookings with filters...');
        const today = new Date();
        setBookingsFilter({
          startDate: new Date(today.getFullYear(), today.getMonth(), 1),
          endDate: new Date(today.getFullYear(), today.getMonth() + 1, 1),
        });
        result = Promise.resolve(undefined);
        break;
    }
    return result;
  };

  useEffect(() => {
    console.log('::::::updated bookingListType: ' + bookingListType);
    refetchBookings();
  }, [bookingListType]);

  const groupedBookings = useMemo(() => {
    return (
      (bookingListType === 'active' && groupAndSortBookings(current)) ||
      (bookingListType === 'archived' && groupAndSortBookings(past)) ||
      (current && groupAndSortBookings(current)) ||
      []
    );
  }, [bookingListType, past, current]);

  useEffect(() => {
    console.log(
      '::::useEffect [selectedBooking] from BookingContext...',
      selectedBooking,
    );
    if (selectedBooking) {
      const booking = (bookingListType === 'archived' ? past : current)?.find(
        (booking: IBooking) => {
          return booking.booking_id === selectedBooking.booking_id;
        },
      );

      console.log('::::found booking: ', booking);
      if (booking) {
        return setSelectedBooking(booking);
      }
    }

    if (groupedBookings?.length) {
      setSelectedBooking(groupedBookings[0]?.bookings[0]);
    }
  }, [groupedBookings]);

  useEffect(() => {
    if (errorFetchingBookings) toggleToast('Failed to get bookings', 'error');
  }, [errorFetchingBookings]);
  /*





 */

  useEffect(() => {
    if (selectedBooking && sessionData)
      setSelectedBooking({ ...selectedBooking, ...sessionData });
  }, [sessionData]);

  useEffect(() => {
    if (updateStatusError)
      toggleToast('Failed to update booking status', 'error');
  }, [updateStatusError]);
  /*
  const [fontSize, setFontSize] = useState(100);
  const html = document.getElementsByTagName('html')[0];
  useEffect(() => {
    //html.style.fontSize = `${(16 * fontSize) / 100}px`;
    //look for applyApplicationFontSize on home/index.tsx
  }, [fontSize]);
  */
  useEffect(() => {
    if (isManageReportModalOpen) refetchSessionDetails();
  }, [isManageReportModalOpen]);

  useEffect(() => {
    console.log('::::useEffect [sessionData]: ', sessionData);

    if (selectedBooking && sessionData)
      setSelectedBooking({ ...selectedBooking, ...sessionData });
  }, [sessionData]);

  useEffect(() => {
    if (updateStatusError)
      toggleToast('Failed to update booking status', 'error');
  }, [updateStatusError]);

  useEffect(() => {
    if (updateDetailsError)
      toggleToast('Failed to update booking retails', 'error');
  }, [updateDetailsError]);

  useEffect(() => {
    if (selectedBooking?.description === undefined) return;

    selectedBooking.description = `${selectedBooking.description
      .replace(contextRegex, '')
      .trim()}\n\nContext: ${context}`;
  }, [context]);

  const getIconStyle = (eventTitle: string): React.CSSProperties => {
    switch (eventTitle) {
      case 'spot session':
        return calendarStyles.SpotBookingIcon;
      case 'Problem Solver':
        return calendarStyles.SpotBookingIcon;
      case 'bundle help':
        return calendarStyles.BundleHelpIcon;
      case 'Home Health Check':
        return calendarStyles.BundleHelpIcon;
      case 'unlimited help':
        return calendarStyles.UnlimitedHelpIcon;
      case 'On Site':
        return calendarStyles.UnlimitedHelpIcon;
      case 'Home 101':
        return calendarStyles.UnlimitedHelpIcon;
      default:
        return {};
    }
  };

  const formatDate = (date: any) => {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    let daySuffix;
    if (day === 1 || day === 21 || day === 31) {
      daySuffix = 'st';
    } else if (day === 2 || day === 22) {
      daySuffix = 'nd';
    } else if (day === 3 || day === 23) {
      daySuffix = 'rd';
    } else {
      daySuffix = 'th';
    }

    return `${month} ${day}${daySuffix}, ${year}`;
  };

  const getLabelForDate = (date?: Date) => {
    if (!date) return '';
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      year: 'numeric',
    };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  };

  const handleToolbarNavigate = (
    action: 'PREV' | 'NEXT' | 'TODAY' | 'DATE',
    newDate?: Date,
  ) => {
    let updatedDate = newDate || currentDate;

    if (action === 'PREV') {
      updatedDate = new Date(currentDate.setMonth(currentDate.getMonth() - 1));
    } else if (action === 'NEXT') {
      updatedDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
    } else if (action === 'TODAY') {
      updatedDate = new Date();
    }
    setCurrentDate(updatedDate);
    setBookingsFilter({
      startDate: new Date(updatedDate.getFullYear(), updatedDate.getMonth(), 1),
      endDate: new Date(
        updatedDate.getFullYear(),
        updatedDate.getMonth() + 1,
        1,
      ),
    });
    setToolbarLabel(getLabelForDate(updatedDate));
  };

  const [selectedPlan, setSelectedPlan] = useState<IPlan | null>(null);
  const [createdBooking, setCreatedBooking] = useState<IBooking | null>(null);
  const [userInfo, setUserInfo] = useState<IUserInfo | null>(null);

  return (
    <BookingsContext.Provider
      value={{
        gitHash,
        isLoading,
        profile,
        updateProfile,
        getUser,

        userSettings,
        updateUserSettings,
        organization,

        bookingsFilter,
        setBookingsFilter,

        bookings,
        isFetchingBookings,
        errorFetchingBookings,

        refetchBookings,
        groupedBookings,

        homeView,
        setHomeView,

        bookingListType,
        setBookingListType,

        isFetchingSessionDetails,

        description,
        yearBuilt,
        purchaseDate,
        contextPresent,
        setContextPresent,
        context,
        setContext,

        isManageReportModalOpen,
        setIsManageReportModalOpen,

        isSessionSummaryModalOpen,
        setIsSessionSummaryModalOpen,

        isBookingModalOpen,
        setIsBookingModalOpen,
        bookingModalUrl,
        setBookingModalUrl,

        selectedBooking,
        setSelectedBooking,

        currentView,
        setCurrentView,

        updateBookingStatus,
        updateStatusError,

        updateDetails,
        updateDetailsLoading,
        updateDetailsError,

        plans,
        setPlans,
        updatePlan,

        services,
        setServices,
        documentTemplates,
        setDocumentTemplates,
        updateDocumentTemplate,
        todaysBookings,

        getIconStyle,

        toolbarLabel,
        setToolbarLabel,

        isDeleteBookingModalOpen,
        setIsDeleteBookingModalOpen,

        filters,
        setFilters,

        filterResults,
        setFilterResults,

        currentDate,
        setCurrentDate,

        handleToolbarNavigate,

        selectedPlan,
        setSelectedPlan,

        createdBooking,
        setCreatedBooking,
        userInfo,
        setUserInfo,

        editBooking,
        setEditBooking,
      }}>
      {children}
    </BookingsContext.Provider>
  );
}

export function useBookingsContext() {
  return useContext(BookingsContext);
}
